import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe, seedDemoData } from '../lib/api';
import { cacheUserData, cacheToken, clearAllData, clearToken, getCachedUserData, getCachedToken, cacheFaceDescriptor, clearCachedFaceDescriptor, getCachedFaceDescriptor } from '../lib/db';
import { useNativeNetwork } from '../hooks/useNativeNetwork';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use Capacitor-aware network hook (native Network plugin or browser events)
  const { isOnline } = useNativeNetwork();

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const cachedUser = localStorage.getItem('user');

        if (token && cachedUser) {
          try {
            const parsedUser = JSON.parse(cachedUser);
            if (isMounted) setUser(parsedUser);
          } catch (e) {
            // Invalid cached user, clear it
            localStorage.removeItem('user');
          }
          
          // Skip token verification - let individual API calls handle 401
          // This prevents the initial 401 loop on page load
        } else if (!token) {
          // No token, ensure user is null
          if (isMounted) setUser(null);
        }
      } catch (error) {
        console.error('Auth init error:', error);
        if (isMounted) setUser(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (nip, password, turnstileToken = null) => {
    if (!isOnline) {
      // Check cached credentials for offline login
      const cachedUser = await getCachedUserData();
      if (cachedUser && cachedUser.nip === nip) {
        setUser(cachedUser);
        return cachedUser;
      }
      throw new Error('Login membutuhkan koneksi internet');
    }

    const response = await apiLogin(nip, password, turnstileToken);
    const { access_token, user: userData } = response;

    // Store in localStorage and IndexedDB
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    await cacheToken(access_token);
    await cacheUserData(userData);
    
    // Cache face descriptor for offline face verification
    if (userData.face_descriptor && Array.isArray(userData.face_descriptor)) {
      await cacheFaceDescriptor(userData.id, userData.face_descriptor);
      console.log('Face descriptor cached for offline verification');
    }

    setUser(userData);
    return userData;
  };

  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    await clearToken();
    await clearCachedFaceDescriptor(); // Clear face descriptor on logout
    await clearAllData();
    setUser(null);
  };

  const initDemoData = async () => {
    try {
      const result = await seedDemoData();
      return result;
    } catch (error) {
      console.error('Failed to seed demo data:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    isOnline,
    login,
    logout,
    initDemoData,
    isAuthenticated: !!user,
    isGuru: user?.role === 'guru',
    isTenagaTataUsaha: user?.role === 'tenaga_tata_usaha',
    isKepalaSekolah: user?.role === 'kepala_sekolah',
    isAdminDinas: user?.role === 'admin_dinas',
    isPimpinan: user?.role === 'pimpinan',
    isEditor: user?.role === 'editor',
    isPengawas: user?.role === 'pengawas',
    isPegawaiInstansi: user?.role === 'pegawai_instansi',
    // Helper for checking if user can do attendance (guru, kepala_sekolah, tenaga_tata_usaha)
    canAbsensi: ['guru', 'kepala_sekolah', 'tenaga_tata_usaha', 'pegawai_instansi'].includes(user?.role) ||
      (['pimpinan', 'editor'].includes(user?.role) && !!user?.instansi_id),
    // Helper for checking multiple roles
    hasRole: (roles) => roles.includes(user?.role),
    canEdit: ['admin_dinas', 'kepala_sekolah'].includes(user?.role)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
